import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { firestoreService } from '../services/firestore.service';
import { cacheService } from '../services/cache.service';
import { createDebouncedSync, initializeFromFirestore, SYNC_TIMING } from '../services/syncHelper';

const basic_theme = {
    font: "Lato,'Helvetica Neue',Arial,Helvetica,sans-serif",
    fontSize: 'big',
    backgroundColor: 'whitesmoke',
    backgroundColorCalendar: '#FFFFFFA0',
    calendarEventsColor: '#000000',
    fontsInitial: {
        'Title': "'Pacifico', cursive",
        'My Task List': "'Rubik', sans-serif",
        'My Task': "'Rubik', sans-serif"
    }
};

export const useAppStore = create(
    persist(
        (set, get) => ({
            // Theme
            colors: 'basic',
            font: basic_theme.font,
            fontSize: basic_theme.fontSize,
            fonts: basic_theme.fontsInitial,
            backgroundColor: basic_theme.backgroundColor,
            backgroundColorCalendar: basic_theme.backgroundColorCalendar,

            // UI State (device-specific, not synced)
            activeSideBarItem: 'tasks',
            designMode: false,
            draggedEvent: null,

            // Firestore sync state
            userId: null,
            isInitialized: false,

            /**
             * Initialize preferences from Firestore
             */
            initializeWithUser: async (userId) => {
                set({ userId });

                await initializeFromFirestore({
                    getCached: () => cacheService.getUser()?.preferences,
                    getFromFirestore: () => firestoreService.getUserPreferences(userId),
                    onData: (prefs) => {
                        if (prefs) {
                            set({
                                colors: prefs.colors || get().colors,
                                font: prefs.font || get().font,
                                fontSize: prefs.fontSize || get().fontSize,
                                fonts: prefs.fonts || get().fonts,
                                backgroundColor: prefs.backgroundColor || get().backgroundColor,
                                backgroundColorCalendar: prefs.backgroundColorCalendar || get().backgroundColorCalendar,
                            });
                        }
                        set({ isInitialized: true });
                    },
                    onError: () => set({ isInitialized: true }),
                });
            },

            /**
             * Sync current preferences to Firestore (debounced)
             */
            syncPreferences: () => {
                const { userId, colors, font, fontSize, fonts, backgroundColor, backgroundColorCalendar } = get();
                if (!userId) return;

                const preferences = { colors, font, fontSize, fonts, backgroundColor, backgroundColorCalendar };

                createDebouncedSync(
                    'preferences',
                    () => firestoreService.updateUserPreferences(userId, preferences),
                    () => cacheService.updateUser({ preferences }),
                    SYNC_TIMING.NORMAL
                )();
            },

            /**
             * Clear user data on logout
             */
            clearUserData: () => {
                set({ userId: null, isInitialized: false });
            },

            // Actions (with Firestore sync)
            setColors: (colors) => {
                set({ colors });
                get().syncPreferences();
            },
            setFont: (font) => {
                set({ font });
                get().syncPreferences();
            },
            setFontSize: (fontSize) => {
                set({ fontSize });
                get().syncPreferences();
            },
            setFonts: (fonts) => {
                set({ fonts });
                get().syncPreferences();
            },
            setBackgroundColor: (backgroundColor) => {
                set({ backgroundColor });
                get().syncPreferences();
            },
            setBackgroundColorCalendar: (backgroundColorCalendar) => {
                set({ backgroundColorCalendar });
                get().syncPreferences();
            },

            // UI State actions (not synced - device-specific)
            setActiveSideBarItem: (item) => set({ activeSideBarItem: item }),
            setDesignMode: (mode) => set({ designMode: mode }),
            setDraggedEvent: (event) => set({ draggedEvent: event }),

            // Reset theme to defaults
            resetTheme: () => {
                set({
                    colors: 'basic',
                    font: basic_theme.font,
                    fontSize: basic_theme.fontSize,
                    fonts: basic_theme.fontsInitial,
                    backgroundColor: basic_theme.backgroundColor,
                    backgroundColorCalendar: basic_theme.backgroundColorCalendar,
                });
                get().syncPreferences();
            },
        }),
        {
            name: 'grownups-app-storage',
            partialize: (state) => ({
                colors: state.colors,
                font: state.font,
                fontSize: state.fontSize,
                fonts: state.fonts,
                backgroundColor: state.backgroundColor,
                backgroundColorCalendar: state.backgroundColorCalendar,
                activeSideBarItem: state.activeSideBarItem,
            }),
        }
    )
);

export { basic_theme };
